import { useMemo } from 'react';
import { CONTACT_FIELDS, KIND, SIDE } from '../../constants';
import { groupOf } from '../state/emptyDoc';
import { isAnswered, timelineStopKeys } from '../state/derive';
import { useWeddingDay } from '../state/useWeddingDay';

const clamp01 = (n) => Math.max(0, Math.min(1, n));

const fraction = (done, total) => (total ? clamp01(done / total) : 0);

/**
 * How far one section is toward being answered. The sections are structural in
 * this form, so each kind measures its own thing, while whether it counts at
 * all stays the payload's call through `section.optional`.
 */
/**
 * The timeline's stops and how full each one is. A stop is a segment of that
 * section's rail, the way a required field is one in the creative form, so
 * the bar moves both when something is answered and when the couple walks on.
 */
export const timelineStops = ({ events, interviews, wantsInterview,
  askInterview }) => {
  // Only the sittings the couple was actually asked for. A group with no
  // event behind it is not offered on the form, so counting it would hold
  // the section open on something they cannot answer.
  const sittings = (interviews || []).filter((x) => (x.dates || []).length);
  const keys = timelineStopKeys({ wantsInterview, askInterview });
  const pct = {
    // Ordering has nothing to measure: it is either still being read or
    // behind them, so it fills the moment they move on.
    order: 0,
    timings: fraction(events.filter(isAnswered).length, events.length),
    interview_ask: wantsInterview === null ? 0 : 1,
    // Answered by its day, which is all a sitting is asked for.
    interviews: fraction(sittings.filter((x) => x.date).length,
      sittings.length),
  };
  return keys.map((key) => ({ key, pct: pct[key] || 0 }));
};

// `walked` is what separates the bar from the gate. The bar credits a stop
// the couple has moved past, so the rail keeps up with them. The gate cannot:
// the server counts answers, so crediting a stop they walked away from
// unanswered is how the sheet came to read complete on a form it then refused.

const sectionPct = ({ section, doc, events, family, limits, interviews,
  contacts, phase, wantsInterview, askInterview, walked = true }) => {
  const kind = section.kind || section.key;
  if (kind === KIND.TIMELINE) {
    const stops = timelineStops({
      events, interviews, wantsInterview, askInterview,
    });
    const at = stops.findIndex((x) => x.key === phase);
    // Ordering is a stop on the rail but not a measure: it has nothing to
    // answer, so counting it as empty held the section short of full even
    // once everything on it was answered.
    const measured = stops.filter((x) => x.key !== 'order');
    const filled = measured.reduce(
      (sum, stop) => sum
        + (walked && stops.indexOf(stop) < at ? 1 : stop.pct), 0
    );
    return fraction(filled, measured.length);
  }
  if (kind === KIND.FAMILY) {
    const sides = [SIDE.BRIDE, SIDE.GROOM];
    // At least one face a side, the same floor the server checks. The
    // section's cap is the most a side may add, not what it must, so a
    // family with fewer people to name is not held short of done.
    const need = limits.min_faces_per_side || 1;
    // Counted by the faces a side picked. The photographs stay on the
    // couple's device, so there is no picture on the payload to look for,
    // and a face keeps its crop after its photograph is taken off.
    const done = sides.filter((s) => {
      const panel = family[s] || {};
      // Only a face whose crop reached the server, which is what `src`
      // says. The crop is the whole of what the team works from, so a
      // face picked but never cut is a tap that did not finish, and
      // counting it had the bar read full on a form the submit refused.
      const picked = (panel.tags || []).filter((t) => t.is_selected && t.src);
      // A face the crew cannot put a name to is a crop they have to guess
      // at, so a cut face is only answered once it says who it is.
      return picked.length >= need
        && picked.every((t) => String(t.relationship || '').trim());
    }).length;
    // Who the crew rings on the day, counted beside the faces so the gate
    // reads the section the way the server does.
    const rows = contacts || [];
    const asked = sides.length * 2;
    const filled = sides.reduce((sum, s) => sum + ['coordinator', 'backup']
      .filter((kind) => {
        const row = rows.find((c) => c.side === s && c.kind === kind) || {};
        return CONTACT_FIELDS[kind].every(
          (f) => String(row[f] || '').trim()
        );
      }).length, 0);
    return fraction(done + filled, sides.length + asked);
  }
  const group = groupOf(doc, section.key);
  if (kind === KIND.NOTES) {
    // A note without an event keeps the section open: our team cannot act on
    // something with no place in the day. Nothing planned is an answer too.
    const notes = (section.activity_kinds || []).reduce(
      (all, akind) => all.concat(Array.isArray(group[akind.key]) ? group[akind.key] : []),
      []
    );
    const written = notes.filter((n) => String(n.description || '').trim());
    if (written.length) return written.every((n) => n.event_id) ? 1 : 0;
    return group.nothing_planned ? 1 : 0;
  }
  const filled = Object.keys(group).filter((k) => String(group[k] ?? '').trim()).length;
  return filled ? 1 : 0;
};

/**
 * One step per section, in payload order, labelled by `section.title`. A
 * section the payload marks optional is excluded from the count and never
 * shown as failed.
 */
export const useProgress = () => {
  const { doc, sections, index, events, family, limits, interviews,
    contacts, needsInterview, phase, wantsInterview,
    askInterview } = useWeddingDay();

  return useMemo(() => {
    const locked = !!doc.submitted;

    const steps = sections.map((section, i) => {
      const pct = sectionPct({
        section, doc, events, family, limits, contacts, phase,
        wantsInterview, askInterview,
        interviews: needsInterview ? interviews : [],
      });
      // What the server will actually count, with nothing credited for
      // having walked on. Only the timeline differs, and only there does
      // the gate disagree with the bar.
      const answered = sectionPct({
        section, doc, events, family, limits, contacts, phase,
        wantsInterview, askInterview, walked: false,
        interviews: needsInterview ? interviews : [],
      });
      const optional = !!section.optional;
      const done = optional ? pct > 0 || locked : answered >= 1;
      const passed = i < index || locked;
      // The bar reports how much of the section is answered. A section left
      // behind still reads full, because walking on from an optional one is
      // an answer in itself.
      const fill = locked || done || (passed && optional) ? 1 : pct;
      // One rail per section, filled by how much of that section is
      // answered. The timeline used to be broken into a segment per stop,
      // which drew the same section as two bars with a gap between them
      // and made its rail read differently from every other one.
      const segments = [{ pct: fill, past: locked || i < index }];
      return {
        key: section.key,
        label: section.title,
        optional,
        index: i,
        pct,
        fill,
        segments,
        done,
        failed: locked && !done && !optional,
        current: i === index,
        dim: !(i <= index || pct > 0),
      };
    });


    // The marker rides the row, so it is resolved against the real column
    // geometry rather than a flat percentage: the grid's own gaps are what
    // pulled it out of step with the rails before.
    const markerLeft = (isPhone) => {
      const gap = isPhone ? 8 : 14;
      const count = steps.length || 1;
      const idx = locked ? count - 1 : index;
      const here = steps[idx];
      // How far along its own rail the current section stands.
      const frac = locked ? 1 : (here ? clamp01(here.fill) : 0);
      return `calc((100% - ${gap * (count - 1)}px) / ${count}`
        + ` * ${(idx + frac).toFixed(4)} + ${gap * idx}px)`;
    };

    // What the submit gate warns about: sections the server will refuse, not
    // the ones the couple was told they could leave blank.
    const incomplete = steps.filter((s) => !s.optional && !s.done).map((s) => s.label);
    const counted = steps.filter((s) => !s.optional);

    return {
      steps,
      markerLeft,
      doneCount: counted.filter((s) => s.done).length,
      countedTotal: counted.length,
      positionLabel: steps[index] ? `Section ${index + 1} of ${steps.length}` : '',
      allComplete: incomplete.length === 0,
      incompleteLabels: incomplete,
    };
  }, [doc, sections, index, events, family, limits, interviews, contacts,
    needsInterview, phase, wantsInterview, askInterview]);
};
